import { Asset } from './asset'
import { AssetType, SessionType } from './assetsInfo'
import { ConditionType } from './conditionType'
import { Money } from './money'
import { OrderTransactionStatusEnum } from './orderTransaction'
import { OrderTypeEnum } from './orderType'

export interface PreOrderRequest {
  shares: number
  type: OrderOperationTypeEnum
  assetId: string
  method: OrderTypeEnum
  price?: number
  expirationDate?: string
  assetMarketSession?: SessionType
  condition?: {
    stopPrice?: number
    trailPercentage?: number
    trailAmount?: number
    stopPercentage?: number
    stopType: ConditionType
    activationPrice?: number
  }
}

export interface OrderRequest {
  preOrderId: string
}

export interface MultiOrderRequest {
  preOrdersId: Array<string>
}

export interface CommissionsData {
  forexFees: {
    amount: Money
    rate: number
  }
  custodyFees: {
    amount: Money
    rate: number
  }
  financialInstrumentFees: {
    amount: Money
    rate: number
  }
  thirdPartyPayments: {
    amount: Money
    rate: number
  }
  executionFees: {
    amount: Money
    rate: number
  }
  totalCommission: Money
}

export interface PreorderCondition {
  stopPercentage?: number
  stopPrice?: number
  trailAmount?: number
  trailPercentage?: number
  stopType: ConditionType
  activationPrice?: number
}

export interface Condition {
  stopPrice?: number
  trailAmount?: number
  trailPercentage?: number
  stopPercentage?: number
  stopType?: ConditionType
  timeInForce?: string
  activationPrice?: number
}

export interface PreOrderData {
  canAutoTransfer: boolean
  id: string
  asset: {
    id: string
    name: string
    isin: string
    ticker: string
    market: string
    currency: string
    session?: SessionType
  }
  targetAssetInfo?: {
    asset?: {
      id?: string
      name?: string
      isin?: string
      ticker?: string
      currency?: string
    }
    units?: number
    netPrice?: Money
  }
  shares: number
  type: OrderOperationTypeEnum
  method: OrderTypeEnum
  expirationDate: string
  cashAvailableForInvesting: Money
  contingenciesBuffer: number
  contingenciesBufferAmount: Money
  enoughCashForOrder: boolean
  fees?: Money
  units: number
  forexRate: number
  netAmount: Money
  netAssetValue?: Money
  netAmountInPortfoliosCurrency: Money
  requiredNetAmount: Money
  grossAmount: Money
  estimatedGrossAmount?: Money
  lockedCash: Money
  marketTaxes: Money
  marketTaxesInfoUrl: string
  price: Money
  commissions: CommissionsData
  totalCost: Money
  condition?: PreorderCondition
  estimatedPnL?: Money
  estimatedNetAmount?: Money
}

export enum PreOrderModalType {
  taxes = 'TAXES',
  info = 'INFO',
  balance = 'BALANCE',
}

export interface AssetOrderData {
  id?: string
  asset?: {
    id?: string
    name?: string
    isin?: string
    ticker?: string
    market?: string
    currency?: string
  }
  targetAssetInfo?: {
    asset?: {
      id?: string
      name?: string
      isin?: string
      ticker?: string
      market?: string
      currency?: string
    }
    units?: number
    netPrice?: Money
    grossAmount?: Money
  }
  shares?: number
  units?: number
  netAssetValue?: Money
  status?: OrderTransactionStatusEnum
  creationDate?: string
  type?: OrderOperationTypeEnum
  method?: OrderTypeEnum
  expirationDate?: string
  estimatedPrice?: Money
  forexRate?: number
  calculations?: {
    contingenciesBuffer?: number
    fees?: Money
    grossAmount?: Money
    lockedCash?: Money
    marketTaxes?: Money
    totalCost?: Money
  }
  commissions?: {
    forexFees?: {
      amount?: Money
      rate?: number
    }
    custodyFees?: {
      amount?: Money
      rate?: number
    }
    financialInstrumentFees?: {
      amount?: Money
      rate?: number
    }
    thirdPartyPayments?: {
      amount?: Money
      rate?: number
    }
    executionFees?: {
      amount?: Money
      rate?: number
    }
    totalCommission?: Money
  }
}

export interface MultipleOrdersResponse {
  errorsDetails: Array<OrderDataMultipleError>
  successDetails: Array<OrderData>
  summary: {
    errors: number
    success: number
  }
}

export interface OrderDataMultipleError {
  apiError?: {
    status?: number
    message?: string
  }
  preOrder?: string
}

export interface OrderData {
  id?: string
  asset?: {
    id?: string
    name?: string
    isin?: string
    ticker?: string
    market?: string
    currency?: string
  }
  shares?: number
  type?: OrderOperationTypeEnum
  method?: OrderTypeEnum
  creationDate?: string
  expirationDate?: string
  estimatedPrice?: Money
  forexRate?: number
  status?: OrderTransactionStatusEnum
  calculations?: {
    contingenciesBuffer?: number
    fees?: Money
    grossAmount?: Money
    lockedCash?: Money
    marketTaxes?: Money
    totalCost?: Money
    estimatedPnL?: Money
  }
  condition?: PreorderCondition
  units?: number
}

export enum OrderOperationTypeEnum {
  buy = 'BUY',
  sell = 'SELL',
  subscribe = 'SUBSCRIBE',
  redeem = 'REDEEM',
  transfer = 'TRANSFER',
}

export enum MoneyOperationTypeEnum {
  addMoney = 'ADD_MONEY',
  cashout = 'CASH_OUT',
  buy = 'BUY',
  sell = 'SELL',
}

export enum OperationTypeEnum {
  buy = 'BUY',
  sell = 'SELL',
  addMoney = 'ADD_MONEY',
  cashout = 'CASH_OUT',
  psd2BankLink = 'psd2BankLink',
}

export enum OperationConfirmationStatus {
  success = 'success',
  cancelled = 'cancelled',
  error = 'error',
}

export enum PaymentMethodTypeEnum {
  card = 'CARD',
  bank = 'BANK', // PSD2
  manualTransfer = 'MANUAL_TRANSFER',
}

export interface BankPaymentReviewDetails {
  concept: string
  amount: number
  currency: string
}
export interface CardPaymentReviewDetails {
  amount: number
  currency: string
}

export interface TopUpReviewDetails {
  method: PaymentMethodTypeEnum
  cardTopUpDetails: CardPaymentReviewDetails
  bankTopUpDetails: BankPaymentReviewDetails
}

export interface SetLimitOrderPayload {
  shares: number
  price: number
  expirationDate: string
}

export interface FundTransferAssetDetail {
  assetId: string
  price: Money
  logo: string
  ticker: string
  type: AssetType
  investmentInfo: {
    minInitial?: Money
    minSuccessive?: Money
    totalExpenseRatio?: number
  }
}

export interface SetFundOrderPayload {
  assetId: string
  units: number
  targetUnits: number
  assetType: AssetType
  type: OrderOperationTypeEnum
  origin: Asset
  target: Asset
  selectFundFrom?: 'portfolio' | 'search'
}

export interface SetMarketOrderPayload {
  shares: number
}

export interface AssetsPreOrderRequest {
  assetId?: string
  method?: OrderTypeEnum // only for buy sell, not funds
  price?: number // limit order
  amount?: number // funds only
  shares?: number
  units?: number
  type: OrderOperationTypeEnum
  assetType: AssetType
  expirationDate?: string //'2021-04-08'
  target?: {
    assetId: string
    price: number
    units: number
  }
}

export interface AssetsPreOrderData {
  id: string
  asset: {
    id: string
    name: string
    isin: string
    ticker: string
    market: string
    currency: string
  }
  shares: number
  units: number
  price: Money
  type: OrderOperationTypeEnum
  method: OrderTypeEnum
  expirationDate: string
  grossAmount: Money
  marketTaxes: Money
  marketTaxesInfoUrl: string
  forexRate: 0
  netAmount: Money
  netAmountInPortfoliosCurrency: Money
  totalCost: Money
  contingenciesBuffer: 0
  contingenciesBufferAmount: Money
  lockedCash: Money
  cashAvailableForInvesting: Money
  commissions: {
    forexFees: {
      amount: Money
      rate: number
    }
    custodyFees: {
      amount: Money
      rate: number
    }
    financialInstrumentFees: {
      amount: Money
      rate: number
    }
    thirdPartyPayments: {
      amount: Money
      rate: number
    }
    executionFees: {
      amount: Money
      rate: number
    }
    totalCommission: Money
  }
  requiredNetAmount: Money
  enoughCashForOrder: true
  canAutoTransfer: boolean
}
